/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useEffect, useState } from 'preact/hooks';
import { clearUrlAndReplace } from '../lib/clearUrlAndReplace';
import { confirmEmail } from '../api';
import { useText } from '@adobe-commerce/elsie/i18n';

interface useEmailConfirmationProps {
  enableEmailConfirmation: boolean;
}

// The client receives a confirmation of the validation via email. When the client follows the provided link, they are directed to the login page with three query parameters. If these parameters are present, we make a request to validate the status. This request returns a response that we display to the user.
export const useEmailConfirmation = ({
  enableEmailConfirmation,
}: useEmailConfirmationProps) => {
  const translations = useText({
    accountConfirmMessage: 'Auth.EmailConfirmationForm.accountConfirmMessage',
    accountConfirmationEmailSuccessMessage:
      'Auth.EmailConfirmationForm.accountConfirmationEmailSuccessMessage',
  });
  const [emailConfirmationStatusMessage, setEmailConfirmationStatusMessage] =
    useState<{
      text: string;
      status: '' | 'success' | 'error';
    }>({
      text: '',
      status: '',
    });

  useEffect(() => {
    if (enableEmailConfirmation) {
      const { search } = window.location;

      if (search.includes('email=') && search.includes('key=')) {
        const validateEmailStatus = async () => {
          const params = new URLSearchParams(search);

          const response = await confirmEmail({
            customerEmail: params.get('email') as string,
            customerConfirmationKey: params.get('key') as string,
          });

          if (!response) return null;

          if (response?.errors?.length) {
            setEmailConfirmationStatusMessage({
              text: response?.errors[0].message,
              status: 'error',
            });
          } else {
            setEmailConfirmationStatusMessage({
              text: response.data.confirmEmail.customer.email
                ? translations.accountConfirmationEmailSuccessMessage.replace(
                    '{email}',
                    response?.data?.confirmEmail.customer?.email
                  )
                : translations.accountConfirmMessage,
              status: 'success',
            });

            clearUrlAndReplace();
          }
        };

        validateEmailStatus();
      }
    }
  }, [enableEmailConfirmation, translations]);

  return { emailConfirmationStatusMessage };
};
